import React, { useState } from 'react';
import './PatientInfoPanel.css';

import { MdContentCopy } from 'react-icons/md';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, UserCheck, X, Check } from 'lucide-react';
import { FaEdit } from 'react-icons/fa';
import DatesAndSlotsPicker from '../../../CommonComponents/Utils/DatesAndSlotsPicker';
import "./DatesAndSlotsPicker.css";
import "./PatientAppointmentSummary.css";
import "./AddPackage.css";
import axios from 'axios';
import { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import { dateTimeFormatter } from '../../../CommonComponents/Utils/DateTimeFormatter';

const PatientAppointmentSummary = ({ patient }) => {

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentApptDate, setCurrentApptDate] = useState(patient.appointmentDate);
  const [currentTimeSlot, setCurrentTimeSlot] = useState(patient.timeSlot);

  const [selectedDate, setSelectedDate] = useState(patient.appointmentDate || new Date().toISOString().split('T')[0]);
  const [selectedSlot, setSelectedSlot] = useState(patient.timeSlot);

  const [isReferralPopupOpen, setIsReferralPopupOpen] = useState(false);
  const [currentReferral, setCurrentReferral] = useState(patient.referral || 'Self');
  const [selectedReferral, setSelectedReferral] = useState(patient.referral || 'Self');

  const referralPresets = ['Self', 'Doctor Referral', 'Walk-In', 'Insurance Partner', 'Other Hospital'];

  const handleSave = async () => {
    try {
      await axios.patch(`${API_BASE_URL}/updateAppointmentTime/${patient.appointmentId}`, {
        appointmentDate: selectedDate,
        timeSlot: selectedSlot,
      });
      setCurrentApptDate(selectedDate);
      setCurrentTimeSlot(selectedSlot);
      toast.success('Appointment time updated successfully!');
    } catch (error) {
      console.error("Error updating appointment:", error);
      toast.error('Failed to update appointment time.');
    }
    setIsPopupOpen(false);
  };

  const handleReferralSave = async () => {
    try {
      await axios.patch(`${API_BASE_URL}/updateReferral/${patient.appointmentId}`, {
        referral: selectedReferral,
      });
      setCurrentReferral(selectedReferral);
      toast.success('Referral updated successfully!');
    } catch (error) {
      console.error("Error updating referral:", error);
      toast.error('Failed to update Referral');
    }
    setIsReferralPopupOpen(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(patient.mrNumber)
      .then(() => toast.success('MR Number copied to clipboard!'))
      .catch(() => toast.error('Failed to copy MR Number'));
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'SCHEDULED':
        return 'scheduled';
      case 'COMPLETED':
        return 'completed';
      case 'CANCELLED':
        return 'cancelled';
      default:
        return 'scheduled';
    }
  };

  return (
    <div className="panel-details-grid">
      <div className="detail-row">
        <span className="detail-label">Doctor Name:</span>
        <span className="detail-value">{patient.doctorName}</span>
      </div>

      <div className="detail-row">
        <span className="detail-label">Appointment Time:</span>
        <div className="detail-value-group">
          <span className="detail-value">
            {dateTimeFormatter(currentApptDate, currentTimeSlot)}
          </span>
          <button
            className="icon-action-badge"
            onClick={() => setIsPopupOpen(true)}
            title="Edit Appointment Time"
          >
            <FaEdit size={13} />
          </button>
        </div>
      </div>

      <div className="detail-row">
        <span className="detail-label">Status:</span>
        <span className={`status-pill ${getStatusClass(patient.appointmentStatus)}`}>
          {patient.appointmentStatus}
        </span>
      </div>

      <div className="detail-row">
        <span className="detail-label">MR No:</span>
        <div className="detail-value-group">
          <span className="detail-value font-mono">{patient.mrNumber}</span>
          <button
            className="icon-action-badge"
            onClick={copyToClipboard}
            title="Copy MR Number"
          >
            <MdContentCopy size={13} />
          </button>
        </div>
      </div>

      <div className="detail-row">
        <span className="detail-label">Referral:</span>
        <div className="detail-value-group">
          <span className="detail-value">{currentReferral || 'Self'}</span>
          <button
            className="icon-action-badge"
            onClick={() => setIsReferralPopupOpen(true)}
            title="Edit Referral"
          >
            <FaEdit size={13} />
          </button>
        </div>
      </div>

      {/* Edit Appointment Time Modal */}
      {isPopupOpen && (
        <div className="modern-modal-overlay" onClick={() => setIsPopupOpen(false)}>
          <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-accent-bar" />
            <div className="modern-modal-header">
              <div className="modal-title-container">
                <div className="modal-icon-badge blue-glow">
                  <Calendar size={20} className="icon-blue" />
                </div>
                <div>
                  <h3 className="modal-main-heading">Edit Appointment Time</h3>
                  <p className="modal-sub-heading">Reschedule date or select a new time slot for this visit</p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setIsPopupOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modern-modal-body">
              <DatesAndSlotsPicker
                selectedDate={selectedDate}
                onDateChange={(date) => setSelectedDate(date)}
                onSlotChange={(slot) => setSelectedSlot(slot)}
              />
            </div>
            <div className="modern-modal-footer">
              <button className="modern-btn-secondary" onClick={() => setIsPopupOpen(false)}>
                Cancel
              </button>
              <button className="modern-btn-primary" onClick={handleSave}>
                <Check size={16} />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Referral Modal */}
      {isReferralPopupOpen && (
        <div className="modern-modal-overlay" onClick={() => setIsReferralPopupOpen(false)}>
          <div className="modern-modal-card animated-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-accent-bar" />
            <div className="modern-modal-header">
              <div className="modal-title-container">
                <div className="modal-icon-badge blue-glow">
                  <UserCheck size={20} className="icon-blue" />
                </div>
                <div>
                  <h3 className="modal-main-heading">Edit Patient Referral</h3>
                  <p className="modal-sub-heading">Update referral source or referring practitioner</p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setIsReferralPopupOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modern-modal-body">
              <div className="modern-field-group">
                <label className="field-label-text">Referral Source / Doctor</label>
                <input
                  type="text"
                  className="modern-form-input"
                  value={selectedReferral}
                  onChange={(e) => setSelectedReferral(e.target.value)}
                  placeholder="Enter Referral (e.g. Self, Dr. Sharma, City Clinic)"
                />
              </div>

              <div style={{ marginTop: '12px' }}>
                <div className="referral-presets-title">Quick Select Presets:</div>
                <div className="referral-chips-group">
                  {referralPresets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`referral-chip-btn ${selectedReferral === preset ? 'active-chip' : ''}`}
                      onClick={() => setSelectedReferral(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modern-modal-footer">
              <button className="modern-btn-secondary" onClick={() => setIsReferralPopupOpen(false)}>
                Cancel
              </button>
              <button className="modern-btn-primary" onClick={handleReferralSave}>
                <Check size={16} />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAppointmentSummary;
